import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import fetcher from '@/core/lib/fetcher'
import {
  ExternalTax,
  TaxAndFeesFormValues,
  TaxAndFeesPolicies,
} from './taxAndFees.types'
import { APIError, APIResponse } from '@/core/types/utils'
import { toast } from '@/core/components/ui/use-toast'

export const taxAndFeesPoliciesQueryOptions = (siteId: string, token: string) =>
  queryOptions({
    queryKey: ['taxAndFeesPolicies', siteId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + `property/${siteId}/tax-policy`)
        .json<Promise<APIResponse<TaxAndFeesPolicies[]>>>(),
  })

export const createTaxAndFeesPolicy = (
  data: TaxAndFeesFormValues,
  siteId: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `property/${siteId}/tax-policy`)
    .post(data)
    .json()

export const updateTaxAndFeesPolicy = (
  data: TaxAndFeesFormValues,
  policyId: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `property/tax-policy/${policyId}`)
    .put(data)
    .json<Promise<APIResponse<TaxAndFeesPolicies>>>()

export const getTaxAndFeesPolicyById = (policyId: string, token: string) =>
  queryOptions({
    queryKey: ['taxAndFeesPolicy', policyId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(
          import.meta.env.VITE_API_BOOKING + `property/tax-policy/${policyId}`
        )
        .get()
        .json<Promise<APIResponse<TaxAndFeesPolicies>>>(),
  })

export const useDeleteTaxAndFeesPolicy = (token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (policyId: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(
          import.meta.env.VITE_API_BOOKING + `property/tax-policy/${policyId}`
        )
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['taxAndFeesPolicies'],
      })
           void queryClient.invalidateQueries({
        queryKey: ['rates'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['rate'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['package'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['packages'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['activities'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['activity'],
      })
    },
    onError: (error) => {
      const errorObject: APIError<null> = JSON.parse(error.message)
      const errorMessage = errorObject.message

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })
}

export const getExternalTaxes = (siteId: string, token: string) =>
  queryOptions({
    queryKey: ['externalTaxes', siteId, token],
    queryFn: () =>
      fetcher

        .get(
          import.meta.env.VITE_API_BOOKING +
            `property/${siteId}/integration/tax`
        )
          .notFound(
          () =>
            ({ data: [], message: '', success: true }) as APIResponse<
              ExternalTax[]
            >
        )
        .json<APIResponse<ExternalTax[]>>(),
  })
